import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5001/api/v1",
  withCredentials: true, // Needed for cookies to be sent with requests
  headers: {
    "Content-Type": "application/json",
  },
});

// Store CSRF token
let csrfToken = null;

// Function to fetch CSRF token
const fetchCsrfToken = async () => {
  if (csrfToken) return csrfToken;

  try {
    const response = await axios.get(
      `${
        process.env.REACT_APP_API_URL || "http://localhost:5001/api/v1"
      }/csrf-token`,
      { withCredentials: true }
    );
    csrfToken = response.data.csrfToken;
    return csrfToken;
  } catch (error) {
    console.error("Failed to fetch CSRF token:", error);
    return null;
  }
};

// Helper to determine if a request needs CSRF token
const needsCsrfToken = (url, method) => {
  const exemptMethods = ["get", "head", "options"];
  if (exemptMethods.includes(method.toLowerCase())) return false;

  const exemptUrls = [
    "/users/login",
    "/users/signup",
    "/users/forgotPassword",
    "/users/resetPassword",
    "/admin/login",
    "/auth/refresh-token",
    "/webhook",
  ];

  return !exemptUrls.some((exemptUrl) => url.includes(exemptUrl));
};

// Request interceptor to add CSRF token when needed
api.interceptors.request.use(
  async (config) => {
    // Check if request needs CSRF token
    if (needsCsrfToken(config.url, config.method)) {
      const token = await fetchCsrfToken();
      if (token) {
        config.headers["X-CSRF-Token"] = token;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if error is due to expired token and not a refresh attempt
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes("/login") &&
      !originalRequest.url.includes("/auth/refresh-token") &&
      !originalRequest.url.includes("/deactivate") &&
      !originalRequest.url.includes("/delete") &&
      !originalRequest.url.includes("/password")
    ) {
      originalRequest._retry = true;

      try {
        // Attempt to refresh token
        // This request will include refreshToken cookie automatically
        const response = await axios.post(
          `${
            process.env.REACT_APP_API_URL || "http://localhost:5001/api/v1"
          }/auth/refresh-token`,
          {},
          { withCredentials: true }
        );

        if (response.data.status === "success") {
          // Cookies are automatically set by server response
          // Just retry the original request
          return api(originalRequest);
        }
      } catch (refreshError) {
        // If refresh token is invalid, redirect to login
        localStorage.removeItem("user");
        window.location.href = "/account-access";
        return Promise.reject(refreshError);
      }
    }

    // Handle token expiration
    if (
      error.response?.status === 401 &&
      !error.config.url.includes("/login") &&
      !error.config.url.includes("/deactivate") &&
      !error.config.url.includes("/delete") &&
      !error.config.url.includes("/password") &&
      error.response?.data?.message?.includes("token")
    ) {
      localStorage.removeItem("user");
      window.location.href = "/account-access";
    }

    return Promise.reject(error);
  }
);

// Auth endpoints
export const login = (credentials) => api.post("/users/login", credentials);
export const register = (userData) => api.post("/users/signup", userData);
export const forgotPassword = (data) => api.post("/users/forgotPassword", data);
export const resetPassword = (token, data) =>
  api.patch(`/users/resetPassword/${token}`, data);
export const refreshAccessToken = () =>
  api.post("/auth/refresh-token", {}, { withCredentials: true });

// User endpoints
export const getCurrentUser = async () => api.get("/users/me");
export const updateProfile = (userData) =>
  api.patch("/users/update-me", userData);
export const updatePassword = (data) =>
  api.patch("/users/update-password", data);
export const logout = () => api.post("/users/logout");
export const upgradeToSmartAgent = () => api.post("/users/upgrade-role");

// Notification endpoints
export const getMyNotifications = () =>
  api.get("/users/notifications/my-notifications");
export const markNotificationAsRead = (id) =>
  api.patch(`/users/notifications/${id}/mark-as-read`);
export const deleteNotification = (id) =>
  api.delete(`/users/notifications/${id}`);

// API Key endpoints
export const getMyApiKeys = () => api.get("/users/api-keys");
export const createApiKey = (data) => api.post("/users/api-keys", data);
export const updateApiKey = (id, data) =>
  api.patch(`/users/api-keys/${id}`, data);
export const deleteApiKey = (id) => api.delete(`/users/api-keys/${id}`);

// Service Plans
export const getDataPlans = () => api.get("/services/data/plans");
export const getCablePlans = () => api.get("/services/cable/plans");
export const getDiscos = () => api.get("/services/electricity/discos");
export const getPriceTiers = () => api.get("/services/price-tiers/public");
export const getPublicPriceTiers = () =>
  api.get("/services/price-tiers/public");

// Result Checker endpoints
export const getResultCheckerPlans = () =>
  api.get("/services/result-checker/plans");

export const buyResultChecker = (data) =>
  api.post("/services/result-checker", data);

// Transactions endpoints
export const getUserTransactions = (filters) =>
  api.get("/services/transactions", {
    params: {
      page: filters.page,
      limit: filters.limit,
      type: filters.type === "all" ? undefined : filters.type,
      status: filters.status === "all" ? undefined : filters.status,
    },
  });

//virtual account endpoint
export const generateVirtualAccount = () => {
  return api.post("/users/generate-virtual-account");
};

// Account Management endpoints
export const deactivateAccount = (password) =>
  api.delete("/users/deactivate", {
    data: {
      password,
      confirmPassword: password, // Add confirmation
    },
  });

export const deleteAccount = (password) =>
  api.delete("/users/delete", {
    data: {
      password,
      confirmPassword: password, // Add confirmation
    },
  });

// Legal document endpoints
export const getLegalTerms = () => api.get("/settings/legal/terms");
export const getLegalPrivacy = () => api.get("/settings/legal/privacy");

export default api;
